import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Progress,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { CANONICAL_TARGETS, HREFLANG_ROLES, SITE_STATUSES } from '@seo/contracts';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import { DeployPanel } from '../components/DeployPanel';
import { useCan } from '../lib/session';
import { CreatePanel } from '../components/CreatePanel';
import { SiteEditor } from './SiteEditor';
import classes from './EntityPage.module.css';

/**
 * Картка сайту — шість вкладок мокапа (рядок 285 і далі).
 *
 * Досі її не існувало як екрана: реєстр показував панель під таблицею, а маршруту `/sites/:id`
 * не було взагалі. Наслідок ширший за незручність — картку не можна було надіслати колезі, не
 * можна було відкрити з дашборда чи з інциденту, і шість станів мокапа (`site:seo`,
 * `site:content`, `site:tech`, `site:gsc`, `site:links`, `site:log`) числились ненабудованими,
 * бо жоден маршрут їх не віддавав.
 *
 * Вкладки — рівно ті шість, і кожна відповідає на своє питання:
 *
 * - **SEO та мови** — що сайт каже про себе пошуковику: канонікал, роль hreflang і група,
 *   яку він справді пише в опублікований HTML;
 * - **Сторінки** — з чого він складається;
 * - **Тех** — що можна змінити, разом із редиректами, які досі не мали жодного екрана;
 * - **GSC** — чи це видно назовні;
 * - **Лінки** — план зовнішніх посилань;
 * - **Журнал змін** — хто і що зробив; у мокапі саме звідси веде «Відкат».
 */

const STATUS_LABELS: Record<string, string> = {
  build: 'збірка',
  live: 'live',
  frozen: 'заморожено',
  error: 'помилка',
};

const STATUS_COLOURS: Record<string, string> = {
  live: 'mint',
  build: 'azure',
  frozen: 'amber',
  error: 'rose',
};

/** Останні 28 днів і попередні 28 — те саме вікно, що на дашборді й у мокапі. */
function windows(): {
  current: { from: string; to: string };
  previous: { from: string; to: string };
} {
  const day = 86_400_000;
  const now = Date.now();
  const iso = (ms: number): string => new Date(ms).toISOString().slice(0, 10);
  return {
    current: { from: iso(now - 28 * day), to: iso(now) },
    previous: { from: iso(now - 56 * day), to: iso(now - 29 * day) },
  };
}

const ENTITY_TYPES = ['brand', 'game', 'cluster'] as const;
type EntityType = (typeof ENTITY_TYPES)[number];
const isEntityType = (v: unknown): v is EntityType => ENTITY_TYPES.includes(v as EntityType);
const isNullableString = (v: unknown): v is string | null => v === null || typeof v === 'string';

/** Патч `sites.update`, зібраний із того, що записав снепшот — тільки змінені поля. */
interface RevertPatch {
  language?: string;
  entityType?: EntityType;
  entityId?: string;
  hostingPoolId?: string | null;
  registrarAccountId?: string | null;
  templateId?: string | null;
  offerLinkId?: string | null;
  canonicalTarget?: (typeof CANONICAL_TARGETS)[number];
  canonicalSiteId?: string | null;
  hreflangRole?: (typeof HREFLANG_ROLES)[number];
  pairedSiteId?: string | null;
  status?: (typeof SITE_STATUSES)[number];
}

/** `diffJson.before` — лише ключі, що справді змінились у цьому запису (`packages/core/src/audit.ts`). */
/**
 * Ідентифікатор масової операції, якщо цей запис поставила вона.
 *
 * Масова операція пише запис у журнал кожного зміненого сайту й кладе туди свій id. Звідси
 * — єдиний вхід у відкат **усієї** операції: панель «Історія операцій» прибрана за планом,
 * бо мокап її не малює, а відкотити одну зміну на одному сайті й відкотити переклейку на
 * сорока доменах — різні дії, і другу не можна було лишити недосяжною.
 */
function bulkOperationOf(diff: unknown): string | null {
  if (diff === null || typeof diff !== 'object') return null;
  const after = (diff as { after?: unknown }).after;
  if (after === null || typeof after !== 'object') return null;
  const id = (after as { bulkOperationId?: unknown }).bulkOperationId;
  return typeof id === 'string' ? id : null;
}

/**
 * Що саме змінилось — рядок, який мокап тримає центральною колонкою журналу.
 *
 * Досі колонка «дія» показувала сирий CRUD (`create`/`update`/`delete`): оператор бачив, що
 * щось сталось, і не бачив що. «canonical: self → drop» і «оновлено» — це різниця між
 * журналом, який читають, і журналом, який гортають.
 *
 * Опис збирається з самого діффу, а не з окремого поля: будь-який окремий текст розійшовся б
 * із тим, що насправді записав `auditedMutation`, і розійшовся б мовчки.
 */
const DEPLOY_CHANGE_LABELS: Record<string, string> = {
  page: 'СТОРІНКА',
  redirect: 'РЕДИРЕКТ',
  settings: 'НАЛАШТУВАННЯ',
};

const FIELD_LABELS: Record<string, string> = {
  canonicalTarget: 'канонікал',
  canonicalSiteId: 'канонікал → сайт',
  hreflangRole: 'роль hreflang',
  pairedSiteId: 'побратим',
  language: 'мова',
  templateId: 'шаблон',
  offerLinkId: 'офер',
  status: 'статус',
  hostingPoolId: 'хостинг-пул',
  registrarAccountId: 'реєстратор',
  entityType: 'тип сутності',
  entityId: 'сутність',
  seoScore: 'SEO-скор',
  indexedPages: 'в індексі',
};

/**
 * Категорія рядка — та сама, що фарбує мокап (CANONICAL · HREFLANG · КОНТЕНТ · ТЕХ · ОФЕР).
 *
 * Виводиться з полів, що змінились, а не зберігається окремо: категорія, яку пише той, хто
 * викликає мутацію, розходиться з фактом при першій же новій процедурі.
 */
const FIELD_KINDS: [readonly string[], string][] = [
  [['canonicalTarget', 'canonicalSiteId'], 'CANONICAL'],
  [['hreflangRole', 'pairedSiteId'], 'HREFLANG'],
  [['language', 'templateId'], 'КОНТЕНТ'],
  [['offerLinkId'], 'ОФЕР'],
  [['hostingPoolId', 'registrarAccountId'], 'ТЕХ'],
  [['status'], 'СТАТУС'],
];

function scalarOf(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'так' : 'ні';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  // Обʼєкт у діффі — вкладена структура (наприклад, набір блоків): показуємо, що вона
  // змінилась, а не «[object Object]», який виглядає як зіпсовані дані.
  return '(складене значення)';
}

function changedFields(diff: unknown): string[] {
  const before = diffBefore(diff) ?? {};
  const after = (diff as { after?: Record<string, unknown> } | null)?.after ?? {};
  return [...new Set([...Object.keys(before), ...Object.keys(after)])].filter(
    (key) => key !== 'bulkOperationId',
  );
}

function entryKind(action: string, diff: unknown): string {
  if (action !== 'update') return action.toUpperCase();
  const fields = changedFields(diff);
  for (const [keys, label] of FIELD_KINDS) {
    if (fields.some((field) => keys.includes(field))) return label;
  }
  return 'ЗМІНА';
}

function entryDescription(action: string, diff: unknown): string {
  if (action !== 'update') return '';
  const before = diffBefore(diff) ?? {};
  const after = (diff as { after?: Record<string, unknown> } | null)?.after ?? {};
  const parts = changedFields(diff).map(
    (field) =>
      `${FIELD_LABELS[field] ?? field}: ${scalarOf(before[field])} → ${scalarOf(after[field])}`,
  );
  return parts.join(' · ');
}

function diffBefore(diff: unknown): Record<string, unknown> | undefined {
  if (diff === null || typeof diff !== 'object') return undefined;
  const before = (diff as { before?: unknown }).before;
  if (before === null || typeof before !== 'object' || Array.isArray(before)) return undefined;
  return before as Record<string, unknown>;
}

/**
 * Відкат до стану «до» одного запису журналу — не до бекапу й не до масової операції, а до
 * снепшота, який `auditedMutation` уже поклав у кожен рядок «оновлено».
 *
 * `entityType`/`entityId` сервер приймає лише парою (`updateSite`): якщо змінився один, а
 * другий у цьому запису не чіпали, добираємо його з поточного стану сайта, інакше відкат
 * впаде на VALIDATION_FAILED там, де сама зміна пройшла без питань.
 */
function revertPatch(
  before: Record<string, unknown> | undefined,
  current: { entityType: string | null; entityId: string | null },
): RevertPatch | null {
  if (before === undefined) return null;
  const patch: RevertPatch = {};

  if (typeof before.language === 'string') patch.language = before.language;
  if (isEntityType(before.entityType)) patch.entityType = before.entityType;
  if (typeof before.entityId === 'string') patch.entityId = before.entityId;
  if (isNullableString(before.hostingPoolId)) patch.hostingPoolId = before.hostingPoolId;
  if (isNullableString(before.registrarAccountId))
    patch.registrarAccountId = before.registrarAccountId;
  if (isNullableString(before.templateId)) patch.templateId = before.templateId;
  if (isNullableString(before.offerLinkId)) patch.offerLinkId = before.offerLinkId;
  if ((CANONICAL_TARGETS as readonly string[]).includes(before.canonicalTarget as string)) {
    patch.canonicalTarget = before.canonicalTarget as (typeof CANONICAL_TARGETS)[number];
  }
  if (isNullableString(before.canonicalSiteId)) patch.canonicalSiteId = before.canonicalSiteId;
  if ((HREFLANG_ROLES as readonly string[]).includes(before.hreflangRole as string)) {
    patch.hreflangRole = before.hreflangRole as (typeof HREFLANG_ROLES)[number];
  }
  if (isNullableString(before.pairedSiteId)) patch.pairedSiteId = before.pairedSiteId;
  if ((SITE_STATUSES as readonly string[]).includes(before.status as string)) {
    patch.status = before.status as (typeof SITE_STATUSES)[number];
  }

  // Один зі звʼязки змінився, другий — ні: добираємо його з того, що на сайті зараз.
  if (
    patch.entityId !== undefined &&
    patch.entityType === undefined &&
    isEntityType(current.entityType)
  ) {
    patch.entityType = current.entityType;
  }
  if (patch.entityType !== undefined && patch.entityId === undefined && current.entityId !== null) {
    patch.entityId = current.entityId;
  }

  return Object.keys(patch).length === 0 ? null : patch;
}

export function SiteCardPage(): React.JSX.Element {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const { siteId } = useParams({ from: '/authed/sites/$siteId' });
  const [tab, setTab] = useState<string | null>('seo');

  const detail = useQuery(trpc.sites.byId.queryOptions({ id: siteId }));

  const range = windows();
  // Кожна вкладка вантажиться лише коли її відкрили: GSC-запит іде в ClickHouse, журнал —
  // у велику таблицю аудиту, і платити за них на кожному відкритті картки нема за що.
  const metrics = useQuery({
    ...trpc.gsc.metrics.queryOptions({ siteId, range: range.current }),
    enabled: tab === 'gsc',
  });
  /** Порівняння приймає перелік сайтів і два вікна — картка питає про один. */
  const compare = useQuery({
    ...trpc.gsc.compare.queryOptions({
      siteIds: [siteId],
      current: range.current,
      previous: range.previous,
    }),
    enabled: tab === 'gsc',
  });
  /**
   * Розрізи по пристроях і країнах.
   *
   * Окремий запит, а не поле в метриках: два агрегати по мільйонах рядків не мають їхати
   * щоразу, коли відкривають графік. Але й ховати їх не можна — сумарна лінія, у якій
   * мобільний провал гаситься десктопним зростанням, виглядає як «усе стабільно».
   */
  const splits = useQuery({
    ...trpc.gsc.splits.queryOptions({ siteId, range: range.current, limit: 10 }),
    enabled: tab === 'gsc',
  });
  /**
   * Правка наявного правила.
   *
   * Досі змінити ціль можна було лише «видалити й створити» — а між двома діями правило не
   * існує, і запит у цю щілину отримує 404 замість переїзду.
   */
  const [editingRedirectId, setEditingRedirectId] = useState<string | null>(null);
  const [redirectDraft, setRedirectDraft] = useState({ fromPath: '', toPath: '' });
  const updateRedirect = useMutation(
    trpc.sites.redirects.update.mutationOptions({
      onSuccess: async () => {
        setEditingRedirectId(null);
        await refreshSite();
      },
    }),
  );

  /** Що саме поїде наступною виливкою — перелік уже зроблених і ще не вилитих змін. */
  const deployQueue = useQuery({
    ...trpc.sites.deployQueue.queryOptions({ id: siteId }),
    enabled: tab === 'tech',
  });

  /** Анкор-профіль домена — з гест-постів на цей сайт, а не з анкорів, що ним володіють. */
  const anchorProfile = useQuery({
    ...trpc.links.siteAnchorProfile.queryOptions({ siteId }),
    enabled: tab === 'links',
  });
  const plans = useQuery({
    ...trpc.links.plans.queryOptions({ siteId }),
    enabled: tab === 'links',
  });
  const history = useQuery({
    ...trpc.audit.history.queryOptions({ entityType: 'site', entityId: siteId, limit: 100 }),
    enabled: tab === 'log',
  });

  const queryClient = useQueryClient();
  const refreshSite = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.sites.pathKey() });
  };
  /** Один спільний мутатор `sites.update`: і для полів SEO-вкладки, і для «Відкату» в журналі. */
  const siteUpdate = useMutation(trpc.sites.update.mutationOptions({ onSuccess: refreshSite }));
  const canWriteSeo = useCan('site_seo', 'write');

  // ── Тех: редиректи домена, досі писала лише масова операція ────────────────────────────
  const [redirectFormOpen, setRedirectFormOpen] = useState(false);
  const redirectForm = useForm({ initialValues: { fromPath: '', toPath: '', code: '301' } });
  const createRedirect = useMutation(
    trpc.sites.redirects.create.mutationOptions({
      onSuccess: async () => {
        redirectForm.reset();
        setRedirectFormOpen(false);
        await refreshSite();
      },
    }),
  );
  const toggleRedirect = useMutation(
    trpc.sites.redirects.setEnabled.mutationOptions({ onSuccess: refreshSite }),
  );
  const deleteRedirect = useMutation(
    trpc.sites.redirects.delete.mutationOptions({ onSuccess: refreshSite }),
  );

  /**
   * Перевірка перед деплоєм — тепер видима на самій картці, не лише у візарді нового сайту.
   * Канонікал у дроп без пари чи в сайт, якого вже нема, сьогодні мовчить до першого деплою;
   * тут наслідок видно до того, як хтось на нього натисне.
   */
  const checklist = useQuery({
    ...trpc.sites.deployChecklist.queryOptions({ id: siteId }),
    enabled: tab === 'tech',
  });

  // ── SEO та мови: ГЕО/мова, шаблон, offer-лінк, перерахунок скора ───────────────────────
  const geos = useQuery({
    ...trpc.geo.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
    enabled: tab === 'seo',
  });
  const templates = useQuery({
    ...trpc.templates.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
    enabled: tab === 'seo',
  });
  const trackers = useQuery({
    ...trpc.infra.trackerLinks.list.queryOptions(),
    enabled: tab === 'seo',
  });
  const computeScore = useMutation(
    trpc.monitoring.computeSeoScore.mutationOptions({ onSuccess: refreshSite }),
  );
  const canComputeScore = useCan('monitoring', 'execute');

  // ── GSC: привʼязка property, синхронізація, інсайти, розрив по мережі ──────────────────
  const gscAccounts = useQuery({ ...trpc.gsc.accounts.queryOptions(), enabled: tab === 'gsc' });
  const linkedProperties = useQuery({
    ...trpc.gsc.properties.queryOptions({ linked: true }),
    enabled: tab === 'gsc',
  });
  const unlinkedProperties = useQuery({
    ...trpc.gsc.properties.queryOptions({ linked: false }),
    enabled: tab === 'gsc',
  });
  const linkGap = useQuery({ ...trpc.gsc.linkGap.queryOptions(), enabled: tab === 'gsc' });
  const linkedProperty = (linkedProperties.data ?? []).find((p) => p.siteId === siteId) ?? null;
  const insights = useQuery({
    ...trpc.gsc.insights.queryOptions({ siteId, range: range.current }),
    enabled: tab === 'gsc' && linkedProperty !== null,
  });
  const canWriteGsc = useCan('site_seo', 'write');

  const refreshGsc = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.gsc.pathKey() });
  };
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
  const linkProperty = useMutation(
    trpc.gsc.linkProperty.mutationOptions({
      onSuccess: async () => {
        setSelectedPropertyId(null);
        await refreshGsc();
      },
    }),
  );
  const syncProperty = useMutation(trpc.gsc.sync.mutationOptions({ onSuccess: refreshGsc }));

  const [gscAccountOpen, setGscAccountOpen] = useState(false);
  const gscAccountForm = useForm({ initialValues: { label: '', email: '' } });
  const createGscAccount = useMutation(
    trpc.gsc.createAccount.mutationOptions({
      onSuccess: async () => {
        gscAccountForm.reset();
        setGscAccountOpen(false);
        await refreshGsc();
      },
    }),
  );
  /** Посилання авторизації живе рівно доти, доки оператор його не відкрив — тримати його в
   * стані запиту сенсу не має, тож просимо його наживо на клік і кладемо в звичайний стан. */
  const [authorizeUrls, setAuthorizeUrls] = useState<Record<string, string>>({});
  const [authorizeError, setAuthorizeError] = useState<string | null>(null);
  const [oauthCode, setOauthCode] = useState<Record<string, string>>({});
  const completeOAuth = useMutation(
    trpc.gsc.completeOAuth.mutationOptions({ onSuccess: refreshGsc }),
  );

  // ── Сторінки: публікація конкретної сторінки ────────────────────────────────────────
  const publishPage = useMutation(trpc.pages.publish.mutationOptions({ onSuccess: refreshSite }));

  // ── Лінки: гест-пости саме цього сайту ──────────────────────────────────────────────
  const guestPosts = useQuery({
    ...trpc.links.guestPosts.queryOptions({ siteId }),
    enabled: tab === 'links',
  });
  const auditPost = useMutation(
    trpc.links.auditPost.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.links.pathKey() });
      },
    }),
  );

  // ── Журнал: відкат до снепшота «до» одного запису ──────────────────────────────────
  const [revertConfirmId, setRevertConfirmId] = useState<string | null>(null);

  if (detail.isPending) {
    return (
      <Box className={classes.panel} data-testid="site-card-loading">
        <Loader size="sm" />
      </Box>
    );
  }

  if (detail.error) {
    return (
      <Box className={classes.panel} data-testid="site-card-error">
        <Text className={classes.panelTitle}>Не вдалося відкрити сайт</Text>
        <Text className={classes.panelBody}>{publicMessageOf(detail.error)}</Text>
      </Box>
    );
  }

  const site = detail.data;

  return (
    <Stack gap="md">
      <Group justify="space-between" data-testid="site-card-header">
        <Group gap={10}>
          <Text className={classes.panelTitle} data-testid="site-card-domain">
            {site.domain}
          </Text>
          <Badge size="sm" color={STATUS_COLOURS[site.status] ?? 'graphite'}>
            {STATUS_LABELS[site.status] ?? site.status}
          </Badge>
          {site.countryCode === null ? null : (
            <Badge size="sm" variant="default">
              {site.countryCode} / {site.language ?? '—'}
            </Badge>
          )}
          {site.abuseFlag ? (
            <Badge size="sm" color="rose" data-testid="site-card-abuse">
              абуз
            </Badge>
          ) : null}
        </Group>
        <Button
          size="compact-xs"
          variant="subtle"
          data-testid="site-card-back"
          onClick={() => {
            void navigate({ to: '/sites' });
          }}
        >
          ← до реєстру
        </Button>
      </Group>

      {/*
       * `keepMounted={false}`: інакше вкладки, яких ніхто не відкривав, тягнуть свої запити
       * одразу — Search Console і журнал змін на кожному відкритті картки.
       */}
      <Tabs keepMounted={false} value={tab} onChange={setTab} data-testid="site-card-tabs">
        <Tabs.List>
          <Tabs.Tab value="seo" data-testid="site-tab-seo">
            SEO та мови
          </Tabs.Tab>
          <Tabs.Tab value="content" data-testid="site-tab-content">
            Сторінки
          </Tabs.Tab>
          <Tabs.Tab value="tech" data-testid="site-tab-tech">
            Тех / переклейки
          </Tabs.Tab>
          <Tabs.Tab value="gsc" data-testid="site-tab-gsc">
            GSC
          </Tabs.Tab>
          <Tabs.Tab value="links" data-testid="site-tab-links">
            Лінки
          </Tabs.Tab>
          <Tabs.Tab value="log" data-testid="site-tab-log">
            Журнал змін
          </Tabs.Tab>
        </Tabs.List>

        <Box mt="md">
          <Tabs.Panel value="seo" data-testid="site-panel-seo">
            <Stack gap="md">
              <Box className={classes.panel} data-testid="site-seo-panel">
                <Group gap="lg" wrap="wrap">
                  <Text className={classes.dim}>
                    сутність <span>{site.entityName ?? '—'}</span>
                  </Text>
                  <Text className={classes.dim}>
                    канонікал <span className={classes.mono}>{site.canonicalTarget}</span>
                  </Text>
                  <Text className={classes.dim}>
                    hreflang <span className={classes.mono}>{site.hreflangRole}</span>
                  </Text>
                  <Text className={classes.dim}>
                    пара <span className={classes.mono}>{site.pairedDomain ?? '—'}</span>
                  </Text>
                  <Text className={classes.dim}>
                    ключів <span className={classes.mono}>{site.keywordCount}</span>
                  </Text>
                </Group>

                {/*
                 * Група — те, що сайт справді пише в опублікований HTML. Вона запікається в
                 * байти на момент рендера, тож побратим, який став live пізніше, у ній
                 * відсутній, і дізнатись про це інакше можна лише перечитавши HTML руками.
                 */}
                <Text className={classes.dim} mt="sm">
                  мовна група{' '}
                  <span className={classes.mono} data-testid="site-card-hreflang-group">
                    {site.hreflangGroup.peers.length === 0
                      ? 'сама'
                      : `${String(site.hreflangGroup.peers.length)} версій`}
                  </span>
                </Text>

                {/*
                 * Матриця мовних версій із мокапа (рядок 3253).
                 *
                 * Раніше тут стояв рядок «домен, домен, домен»: три з чотирьох полів, які
                 * `hreflangPeersOf` уже віддає, викидались. А питання в цьому місці звучить
                 * «яка мова на якому ринку вже є, і чого бракує» — без мови й ринку на нього
                 * не відповісти, і оператор ішов звіряти реєстр руками.
                 */}
                {site.hreflangGroup.peers.length === 0 ? null : (
                  <Table mt={6} data-testid="site-hreflang-matrix">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>домен</Table.Th>
                        <Table.Th>ГЕО</Table.Th>
                        <Table.Th>мова</Table.Th>
                        <Table.Th>роль</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {site.hreflangGroup.peers.map((peer) => (
                        <Table.Tr key={peer.domain} data-testid={`hreflang-peer-${peer.domain}`}>
                          <Table.Td className={classes.mono}>{peer.domain}</Table.Td>
                          <Table.Td className={classes.mono}>{peer.countryCode ?? '—'}</Table.Td>
                          <Table.Td className={classes.mono}>{peer.language}</Table.Td>
                          <Table.Td className={classes.dim}>{peer.role}</Table.Td>
                        </Table.Tr>
                      ))}
                      {/*
                       * «— не створено —» з мокапа: ринок і мова, які бренд оголосив, а сайту
                       * під них немає. Матриця без цих рядків показує, що є, і не показує,
                       * чого бракує — а дивляться саме на друге.
                       */}
                      {site.hreflangGroup.gaps.map((gap) => (
                        <Table.Tr
                          key={`${gap.geoId}-${gap.language}`}
                          data-testid={`hreflang-gap-${gap.countryCode}-${gap.language}`}
                        >
                          <Table.Td className={classes.dim}>— не створено —</Table.Td>
                          <Table.Td className={classes.mono}>{gap.countryCode}</Table.Td>
                          <Table.Td className={classes.mono}>{gap.language}</Table.Td>
                          <Table.Td className={classes.warn}>черга</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}

                {/*
                 * «Додати ГЕО/мову» веде у візард, а не відкриває другу форму створення
                 * сайту: мовна версія — це окремий сайт із власним доменом, і дублювати тут
                 * половину візарда означало б завести другий шлях, який розійдеться з першим.
                 */}
                <Button
                  size="compact-xs"
                  variant="default"
                  mt={6}
                  data-testid="site-hreflang-add"
                  onClick={() => {
                    void navigate({ to: '/sites/new' });
                  }}
                >
                  Додати мовну версію
                </Button>
                {site.hreflangGroup.stale ? (
                  <Text className={classes.warn} mt={4} data-testid="site-card-hreflang-stale">
                    опубліковану групу застаріло: побратим став live після цього деплою — задеплойте
                    сайт ще раз
                  </Text>
                ) : null}
              </Box>

              {/*
               * ГЕО фіксується на створенні сайта (`sites.create`): від нього залежить дозволений
               * список мов, hreflang-група та ключі, і `sites.update` навмисно не приймає його —
               * переставити сайт в інше ГЕО означає завести інший сайт, не відредагувати цей.
               * Мова, шаблон і offer-лінк лишаються редагованими поштучно.
               */}
              <Box className={classes.panel} data-testid="site-seo-edit-panel">
                <Text className={classes.panelTitle} mb={8}>
                  ГЕО та мова, шаблон, offer-лінк
                </Text>
                <Group gap="sm" align="flex-end" wrap="wrap">
                  <Select
                    size="xs"
                    label="ГЕО"
                    disabled
                    value={site.geoId}
                    data={
                      site.geoId === null
                        ? []
                        : [{ value: site.geoId, label: site.countryCode ?? site.geoId }]
                    }
                    data-testid="site-seo-geo"
                    style={{ width: 120 }}
                  />
                  <Select
                    size="xs"
                    label="Мова"
                    disabled={!canWriteSeo || site.geoId === null}
                    searchable
                    value={site.language}
                    data={(geos.data?.items.find((g) => g.id === site.geoId)?.languages ?? []).map(
                      (code) => ({
                        value: code,
                        label: code,
                      }),
                    )}
                    data-testid="site-seo-language"
                    style={{ width: 110 }}
                    onChange={(value) => {
                      if (value !== null)
                        siteUpdate.mutate({ id: site.id, patch: { language: value } });
                    }}
                  />
                  <Select
                    size="xs"
                    label="Шаблон"
                    disabled={!canWriteSeo}
                    searchable
                    clearable
                    value={site.templateId}
                    data={(templates.data?.items ?? []).map((t) => ({
                      value: t.id,
                      label: t.name,
                    }))}
                    data-testid="site-seo-template"
                    style={{ width: 220 }}
                    onChange={(value) => {
                      siteUpdate.mutate({ id: site.id, patch: { templateId: value } });
                    }}
                  />
                  <Select
                    size="xs"
                    label="Offer-лінк"
                    disabled={!canWriteSeo}
                    searchable
                    clearable
                    value={site.offerLinkId}
                    data={(trackers.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
                    data-testid="site-seo-offer-link"
                    style={{ width: 220 }}
                    onChange={(value) => {
                      siteUpdate.mutate({ id: site.id, patch: { offerLinkId: value } });
                    }}
                  />
                </Group>
                {siteUpdate.error === null ? null : (
                  <Text
                    className={classes.danger}
                    mt={6}
                    data-testid="site-seo-edit-error"
                    role="alert"
                  >
                    {publicMessageOf(siteUpdate.error)}
                  </Text>
                )}
              </Box>

              {/*
               * `monitoring.computeSeoScore` існував без жодного викликача — сайт мав скор
               * у базі, який ніхто не міг перерахувати з екрана, лише з крону.
               */}
              {canComputeScore ? (
                <Box className={classes.panel} data-testid="site-seo-score-panel">
                  <Group gap="sm" align="center">
                    <Text className={classes.dim}>
                      SEO-скор{' '}
                      <span className={classes.mono} data-testid="site-seo-score-current">
                        {site.seoScore ?? '—'}
                      </span>
                    </Text>
                    <Button
                      size="compact-xs"
                      variant="default"
                      loading={computeScore.isPending}
                      data-testid="site-seo-score-recompute"
                      onClick={() => {
                        computeScore.mutate({ siteId: site.id, date: range.current.to });
                      }}
                    >
                      Перерахувати
                    </Button>
                    {computeScore.data === undefined ? null : (
                      <Text className={classes.mono} data-testid="site-seo-score-result">
                        нове значення: {computeScore.data.total}
                      </Text>
                    )}
                  </Group>
                  {computeScore.error === null ? null : (
                    <Text
                      className={classes.danger}
                      mt={6}
                      data-testid="site-seo-score-error"
                      role="alert"
                    >
                      {publicMessageOf(computeScore.error)}
                    </Text>
                  )}
                </Box>
              ) : null}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="content" data-testid="site-panel-content">
            <Box className={classes.panel} data-testid="site-content-panel">
              {/*
               * «ЗБІРКА» — кнопка мокапа саме на цій вкладці. Без неї візард збірки не мав
               * входу з застосунку взагалі: рядок сайдбару прибрано за планом, і дістатись
               * туди можна було лише набравши адресу, вибравши там сайт удруге.
               */}
              <Group justify="space-between" mb={8}>
                <Text className={classes.panelTitle}>Сторінки</Text>
                <Button
                  size="xs"
                  variant="default"
                  data-testid="site-content-build"
                  onClick={() => {
                    void navigate({ to: '/build', search: { siteId: site.id } });
                  }}
                >
                  Збірка
                </Button>
              </Group>
              {site.pages.length === 0 ? (
                <Text className={classes.danger} data-testid="site-card-no-pages">
                  Сторінок немає — сайт ще не збирався
                </Text>
              ) : (
                <Table data-testid="site-card-pages">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>url</Table.Th>
                      <Table.Th>тип</Table.Th>
                      <Table.Th>слів</Table.Th>
                      <Table.Th>ключів</Table.Th>
                      <Table.Th>статус</Table.Th>
                      <Table.Th>дії</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {site.pages.map((page) => (
                      <Table.Tr key={page.id} data-testid={`site-card-page-${page.url}`}>
                        <Table.Td className={classes.mono}>{page.url}</Table.Td>
                        <Table.Td className={classes.dim}>{page.pageType}</Table.Td>
                        <Table.Td className={classes.mono}>{page.words}</Table.Td>
                        <Table.Td className={classes.mono}>{page.keywordsCount}</Table.Td>
                        <Table.Td>
                          <Badge size="sm" variant="default">
                            {page.status}
                          </Badge>
                          {page.publishedVersion === page.version ? null : (
                            <Badge
                              size="sm"
                              color="amber"
                              ml={4}
                              data-testid={`site-page-unpublished-${page.url}`}
                            >
                              є неопубліковане
                            </Badge>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Group gap={6} wrap="nowrap">
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              data-testid={`site-page-edit-${page.url}`}
                              onClick={() => {
                                void navigate({
                                  to: '/editor',
                                  search: { siteId: site.id, pageId: page.id },
                                });
                              }}
                            >
                              Редактор
                            </Button>
                            <Button
                              size="compact-xs"
                              variant="default"
                              loading={
                                publishPage.isPending && publishPage.variables?.id === page.id
                              }
                              disabled={page.publishedVersion === page.version}
                              data-testid={`site-page-publish-${page.url}`}
                              onClick={() => {
                                publishPage.mutate({
                                  id: page.id,
                                  expectedVersion: page.version,
                                });
                              }}
                            >
                              Опублікувати
                            </Button>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
              {publishPage.error === null ? null : (
                <Text
                  className={classes.danger}
                  mt={8}
                  data-testid="site-content-publish-error"
                  role="alert"
                >
                  {publicMessageOf(publishPage.error)}
                </Text>
              )}
              <Text className={classes.dim} mt={8} data-testid="site-content-editor-note">
                «Редактор» відкриває спільний екран редактора сторінок — сайт і сторінку там
                обирають ще раз, прямого переходу на конкретну сторінку поки немає.
              </Text>
            </Box>
          </Tabs.Panel>

          <Tabs.Panel value="tech" data-testid="site-panel-tech">
            <Stack gap="md">
              <SiteEditor
                key={site.id}
                row={{
                  id: site.id,
                  domain: site.domain,
                  status: site.status,
                  canonicalTarget: site.canonicalTarget,
                  canonicalSiteId: site.canonicalSiteId,
                  hreflangRole: site.hreflangRole,
                  pairedSiteId: site.pairedSiteId,
                  templateId: site.templateId,
                  hostingPoolId: site.hostingPoolId,
                  hostingPoolName: site.hostingPoolName,
                  registrarAccountId: site.registrarAccountId,
                }}
                onClose={() => {
                  void navigate({ to: '/sites' });
                }}
              />

              {/*
               * Перевірка перед деплоєм, порахована з даних, а не намальована в мокапі: канонікал
               * у дроп без пари чи в сайт, якого вже нема, тут видно ДО того, як хтось задеплоїть
               * і дізнається про це з `BUILD_STEP_BLOCKED`.
               */}
              <Box className={classes.panel} data-testid="site-checklist-panel">
                <Text className={classes.panelTitle} mb={8}>
                  Перевірка перед деплоєм
                </Text>
                {checklist.error ? (
                  <Text className={classes.danger} data-testid="site-checklist-error">
                    {publicMessageOf(checklist.error)}
                  </Text>
                ) : checklist.data === undefined ? (
                  <Loader size="sm" />
                ) : (
                  <Stack gap={6}>
                    {checklist.data.checks.map((check) => (
                      <Group key={check.key} gap={8} data-testid={`site-checklist-${check.key}`}>
                        <Badge
                          size="sm"
                          color={
                            check.level === 'ok'
                              ? 'mint'
                              : check.level === 'warn'
                                ? 'amber'
                                : 'rose'
                          }
                        >
                          {check.level === 'ok'
                            ? 'ОК'
                            : check.level === 'warn'
                              ? 'ПЕРЕВІРИТИ'
                              : 'НЕ ГОТОВО'}
                        </Badge>
                        <Text size="sm">{check.label}</Text>
                        <Text className={classes.dim}>{check.detail}</Text>
                      </Group>
                    ))}
                    <Text
                      className={checklist.data.deployable ? classes.dim : classes.danger}
                      data-testid="site-checklist-deployable"
                    >
                      {checklist.data.deployable
                        ? 'Відмов немає — можна деплоїти'
                        : 'Є відмови — деплой впаде на першій із них'}
                    </Text>
                  </Stack>
                )}
              </Box>

              {/*
               * Редиректи писала лише масова операція — виправити одне правило на одному
               * домені означало запускати масову операцію на один сайт.
               */}
              {/*
               * Кнопка виливки стоїть поруч із чеклістом і чергою — трьома панелями поспіль
               * читається «чи можна · що поїде · вилити». Компонент той самий, що у візарді:
               * виливка мусить мати один набір запобіжників, а друга копія розійшлася б із
               * першою саме в них.
               */}
              {checklist.data === undefined ? null : (
                <DeployPanel
                  siteId={site.id}
                  deployable={checklist.data.deployable}
                  testId="site-deploy"
                />
              )}

              {/*
               * «Черга на наступний деплой» з мокапа. Чеклист вище відповідає «чи можна
               * виливати», ця панель — «що саме виллється»: без неї «Задеплоїти» тиснеться
               * наосліп, а зміст зміни дізнаються постфактум із диффа.
               */}
              <Box className={classes.panel} data-testid="site-deploy-queue-panel">
                <Text className={classes.panelTitle} mb={8}>
                  Черга на наступний деплой
                </Text>
                {deployQueue.error ? (
                  <Text className={classes.danger} data-testid="site-deploy-queue-error">
                    {publicMessageOf(deployQueue.error)}
                  </Text>
                ) : deployQueue.data === undefined ? (
                  <Loader size="sm" />
                ) : deployQueue.data.changes.length === 0 ? (
                  <Text className={classes.dim} data-testid="site-deploy-queue-empty">
                    {deployQueue.data.deployedAt === null
                      ? 'Сайт ще не виливався, і змін теж немає — виливати нічого.'
                      : 'Від останньої виливки нічого не змінилось.'}
                  </Text>
                ) : (
                  <Stack gap={4}>
                    {deployQueue.data.changes.map((change, index) => (
                      <Group
                        key={`${change.kind}-${String(index)}`}
                        gap={10}
                        data-testid={`deploy-change-${change.kind}-${String(index)}`}
                      >
                        <Badge size="xs" variant="default">
                          {DEPLOY_CHANGE_LABELS[change.kind] ?? change.kind}
                        </Badge>
                        <Text className={classes.dim}>{change.label}</Text>
                      </Group>
                    ))}
                  </Stack>
                )}
              </Box>

              <Box className={classes.panel} data-testid="site-redirects-panel">
                <Group justify="space-between" mb={8}>
                  <Text className={classes.panelTitle}>Редиректи</Text>
                  <CreatePanel
                    label="+ правило"
                    testId="redirect"
                    zone="site_seo"
                    open={redirectFormOpen}
                    onOpenChange={setRedirectFormOpen}
                    busy={createRedirect.isPending}
                    error={createRedirect.error}
                    onReset={() => {
                      createRedirect.reset();
                      redirectForm.reset();
                    }}
                    onSubmit={redirectForm.onSubmit((values) => {
                      const code = Number(values.code);
                      createRedirect.mutate({
                        siteId: site.id,
                        fromPath: values.fromPath,
                        toPath: values.toPath,
                        ...(Number.isFinite(code) ? { code } : {}),
                      });
                    })}
                  >
                    <TextInput
                      size="xs"
                      label="Звідки"
                      placeholder="/old/"
                      data-testid="redirect-from"
                      style={{ width: 160 }}
                      {...redirectForm.getInputProps('fromPath')}
                    />
                    <TextInput
                      size="xs"
                      label="Куди"
                      placeholder="/"
                      data-testid="redirect-to"
                      style={{ width: 160 }}
                      {...redirectForm.getInputProps('toPath')}
                    />
                    <TextInput
                      size="xs"
                      label="Код"
                      placeholder="301"
                      data-testid="redirect-code"
                      style={{ width: 70 }}
                      {...redirectForm.getInputProps('code')}
                    />
                  </CreatePanel>
                </Group>
                {site.redirects.length === 0 ? (
                  <Text className={classes.dim} data-testid="site-card-no-redirects">
                    Редиректів немає
                  </Text>
                ) : (
                  <Table data-testid="site-card-redirects">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>звідки</Table.Th>
                        <Table.Th>куди</Table.Th>
                        <Table.Th>код</Table.Th>
                        <Table.Th>стан</Table.Th>
                        <Table.Th>дії</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {site.redirects.map((redirect) => (
                        <Table.Tr
                          key={redirect.id}
                          data-testid={`site-redirect-${redirect.fromPath}`}
                        >
                          <Table.Td className={classes.mono}>
                            {editingRedirectId === redirect.id ? (
                              <TextInput
                                size="xs"
                                value={redirectDraft.fromPath}
                                onChange={(event) => {
                                  setRedirectDraft((draft) => ({
                                    ...draft,
                                    fromPath: event.currentTarget.value,
                                  }));
                                }}
                                data-testid={`site-redirect-from-${redirect.fromPath}`}
                              />
                            ) : (
                              redirect.fromPath
                            )}
                          </Table.Td>
                          <Table.Td className={classes.mono}>
                            {editingRedirectId === redirect.id ? (
                              <TextInput
                                size="xs"
                                value={redirectDraft.toPath}
                                onChange={(event) => {
                                  setRedirectDraft((draft) => ({
                                    ...draft,
                                    toPath: event.currentTarget.value,
                                  }));
                                }}
                                data-testid={`site-redirect-to-${redirect.fromPath}`}
                              />
                            ) : (
                              redirect.toPath
                            )}
                          </Table.Td>
                          <Table.Td className={classes.mono}>{redirect.code}</Table.Td>
                          <Table.Td>
                            <Badge size="sm" color={redirect.enabled ? 'mint' : 'graphite'}>
                              {redirect.enabled ? 'увімкнено' : 'вимкнено'}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Group gap={6} wrap="nowrap">
                              {editingRedirectId === redirect.id ? (
                                <Button
                                  size="compact-xs"
                                  loading={updateRedirect.isPending}
                                  data-testid={`site-redirect-save-${redirect.fromPath}`}
                                  onClick={() => {
                                    updateRedirect.mutate({
                                      id: redirect.id,
                                      fromPath: redirectDraft.fromPath,
                                      toPath: redirectDraft.toPath,
                                    });
                                  }}
                                >
                                  Зберегти
                                </Button>
                              ) : (
                                <Button
                                  size="compact-xs"
                                  variant="subtle"
                                  data-testid={`site-redirect-edit-${redirect.fromPath}`}
                                  onClick={() => {
                                    setEditingRedirectId(redirect.id);
                                    setRedirectDraft({
                                      fromPath: redirect.fromPath,
                                      toPath: redirect.toPath,
                                    });
                                  }}
                                >
                                  Змінити
                                </Button>
                              )}
                              <Button
                                size="compact-xs"
                                variant="subtle"
                                data-testid={`site-redirect-toggle-${redirect.fromPath}`}
                                onClick={() => {
                                  toggleRedirect.mutate({
                                    id: redirect.id,
                                    enabled: !redirect.enabled,
                                  });
                                }}
                              >
                                {redirect.enabled ? 'вимкнути' : 'увімкнути'}
                              </Button>
                              <Button
                                size="compact-xs"
                                variant="subtle"
                                color="rose"
                                data-testid={`site-redirect-delete-${redirect.fromPath}`}
                                onClick={() => {
                                  deleteRedirect.mutate({ id: redirect.id });
                                }}
                              >
                                видалити
                              </Button>
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
                {toggleRedirect.error === null && deleteRedirect.error === null ? null : (
                  <Text
                    className={classes.danger}
                    mt={8}
                    data-testid="site-redirect-error"
                    role="alert"
                  >
                    {publicMessageOf(toggleRedirect.error ?? deleteRedirect.error)}
                  </Text>
                )}
              </Box>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="gsc" data-testid="site-panel-gsc">
            <Stack gap="md">
              {/*
               * `gsc.linkGap` — не про цей сайт, а про мережу: скільки властивостей Search
               * Console досі не привʼязано до жодного сайту. Контекст поруч із власним
               * статусом сайту, а не замість нього.
               */}
              {linkGap.data === undefined ? null : (
                <Text className={classes.dim} data-testid="site-gsc-link-gap">
                  у мережі привʼязано {linkGap.data.linked} з {linkGap.data.total} властивостей
                  Search Console
                </Text>
              )}

              <Box className={classes.panel} data-testid="site-gsc-link-panel">
                <Text className={classes.panelTitle} mb={8}>
                  Property Search Console
                </Text>
                {linkedProperty === null ? (
                  <Group gap="sm" align="flex-end" wrap="wrap">
                    <Select
                      size="xs"
                      label="Привʼязати property"
                      placeholder="не привʼязано"
                      searchable
                      disabled={!canWriteGsc}
                      value={selectedPropertyId}
                      onChange={setSelectedPropertyId}
                      data={(unlinkedProperties.data ?? []).map((p) => ({
                        value: p.id,
                        label: `${p.propertyUrl} (${p.accountEmail})`,
                      }))}
                      data-testid="site-gsc-property-select"
                      style={{ width: 320 }}
                    />
                    <Button
                      size="compact-xs"
                      disabled={selectedPropertyId === null}
                      loading={linkProperty.isPending}
                      data-testid="site-gsc-link-property"
                      onClick={() => {
                        if (selectedPropertyId !== null) {
                          linkProperty.mutate({ propertyId: selectedPropertyId, siteId: site.id });
                        }
                      }}
                    >
                      Привʼязати
                    </Button>
                  </Group>
                ) : (
                  <Group gap="sm" align="center" wrap="wrap">
                    <Text className={classes.mono} data-testid="site-gsc-linked-property">
                      {linkedProperty.propertyUrl}
                    </Text>
                    <Badge size="sm" color={linkedProperty.error === null ? 'mint' : 'rose'}>
                      {linkedProperty.syncStatus}
                    </Badge>
                    <Button
                      size="compact-xs"
                      variant="default"
                      loading={syncProperty.isPending}
                      data-testid="site-gsc-sync"
                      onClick={() => {
                        syncProperty.mutate({
                          propertyId: linkedProperty.id,
                          range: range.current,
                        });
                      }}
                    >
                      Синхронізувати
                    </Button>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      disabled={!canWriteGsc}
                      data-testid="site-gsc-unlink-property"
                      onClick={() => {
                        linkProperty.mutate({ propertyId: linkedProperty.id, siteId: null });
                      }}
                    >
                      Відвʼязати
                    </Button>
                  </Group>
                )}
                {linkProperty.error === null && syncProperty.error === null ? null : (
                  <Text
                    className={classes.danger}
                    mt={6}
                    data-testid="site-gsc-link-error"
                    role="alert"
                  >
                    {publicMessageOf(linkProperty.error ?? syncProperty.error)}
                  </Text>
                )}
              </Box>

              <Box className={classes.panel} data-testid="site-gsc-panel">
                {metrics.isPending || compare.isPending ? (
                  <Loader size="sm" />
                ) : (metrics.error ?? compare.error) ? (
                  <Text className={classes.danger} data-testid="site-gsc-error">
                    {publicMessageOf(metrics.error ?? compare.error)}
                  </Text>
                ) : (
                  <Stack gap="sm">
                    <Group gap="lg">
                      <Text className={classes.dim}>
                        кліки{' '}
                        <span className={classes.mono} data-testid="site-gsc-clicks">
                          {metrics.data?.summary.clicks ?? 0}
                        </span>
                      </Text>
                      <Text className={classes.dim}>
                        покази{' '}
                        <span className={classes.mono} data-testid="site-gsc-impressions">
                          {metrics.data?.summary.impressions ?? 0}
                        </span>
                      </Text>
                      <Text className={classes.dim}>
                        позиція{' '}
                        <span className={classes.mono} data-testid="site-gsc-position">
                          {metrics.data?.summary.position ?? 0}
                        </span>
                      </Text>
                      <Text className={classes.dim}>
                        було за попередні 28 днів{' '}
                        <span className={classes.mono} data-testid="site-gsc-previous">
                          {compare.data?.previous.clicks ?? 0}
                        </span>
                      </Text>
                    </Group>

                    {(metrics.data?.topQueries ?? []).length === 0 ? (
                      <Text className={classes.dim} data-testid="site-gsc-empty">
                        Search Console не має даних за це вікно — property не привʼязано або ще не
                        синхронізовано.
                      </Text>
                    ) : (
                      <Table data-testid="site-gsc-queries">
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>запит</Table.Th>
                            <Table.Th>кліки</Table.Th>
                            <Table.Th>покази</Table.Th>
                            <Table.Th>позиція</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {(metrics.data?.topQueries ?? []).map((row) => (
                            <Table.Tr key={row.query} data-testid={`site-gsc-query-${row.query}`}>
                              <Table.Td>{row.query}</Table.Td>
                              <Table.Td className={classes.mono}>{row.clicks}</Table.Td>
                              <Table.Td className={classes.mono}>{row.impressions}</Table.Td>
                              <Table.Td className={classes.mono}>{row.position}</Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    )}
                  </Stack>
                )}
              </Box>

              {linkedProperty === null ? null : (
                <Box className={classes.panel} data-testid="site-gsc-splits-panel">
                  <Text className={classes.panelTitle} mb={8}>
                    Пристрої та країни
                  </Text>
                  {splits.error ? (
                    <Text className={classes.danger} data-testid="site-gsc-splits-error">
                      {publicMessageOf(splits.error)}
                    </Text>
                  ) : splits.data === undefined ? (
                    <Loader size="sm" />
                  ) : splits.data.devices.length === 0 && splits.data.countries.length === 0 ? (
                    <Text className={classes.dim} data-testid="site-gsc-splits-empty">
                      За цей період Search Console не віддала жодного рядка.
                    </Text>
                  ) : (
                    <Group align="flex-start" gap="xl">
                      <Stack gap={4} data-testid="site-gsc-devices">
                        {splits.data.devices.map((row) => (
                          <Group key={row.key} gap={10} data-testid={`gsc-device-${row.key}`}>
                            <Text className={classes.mono} fz="xs" style={{ width: 90 }}>
                              {row.key}
                            </Text>
                            <Text className={classes.dim}>
                              {row.clicks} кліків · CTR {(row.ctr * 100).toFixed(1)}% · поз.{' '}
                              {row.position.toFixed(1)}
                            </Text>
                          </Group>
                        ))}
                      </Stack>
                      <Stack gap={4} data-testid="site-gsc-countries">
                        {splits.data.countries.map((row) => (
                          <Group key={row.key} gap={10} data-testid={`gsc-country-${row.key}`}>
                            <Text className={classes.mono} fz="xs" style={{ width: 90 }}>
                              {row.key}
                            </Text>
                            <Text className={classes.dim}>
                              {row.clicks} кліків · CTR {(row.ctr * 100).toFixed(1)}% · поз.{' '}
                              {row.position.toFixed(1)}
                            </Text>
                          </Group>
                        ))}
                      </Stack>
                    </Group>
                  )}
                </Box>
              )}

              {/*
               * `gsc.insights` не мав викликача: чотири готові підбірки (`striking`,
               * `cannibalization`, `withoutPage`, `lowCtr`) рахувались і нікуди не йшли.
               */}
              {linkedProperty === null ? null : (
                <Box className={classes.panel} data-testid="site-gsc-insights-panel">
                  <Text className={classes.panelTitle} mb={8}>
                    Інсайти
                  </Text>
                  {insights.error ? (
                    <Text className={classes.danger} data-testid="site-gsc-insights-error">
                      {publicMessageOf(insights.error)}
                    </Text>
                  ) : insights.data === undefined ? (
                    <Loader size="sm" />
                  ) : (
                    <Stack gap={4}>
                      <Text className={classes.dim} data-testid="site-gsc-insights-striking">
                        на межі топ-10: {insights.data.striking.length}
                      </Text>
                      <Text className={classes.dim} data-testid="site-gsc-insights-cannibalization">
                        канібалізація: {insights.data.cannibalization.length}
                      </Text>
                      <Text className={classes.dim} data-testid="site-gsc-insights-without-page">
                        без сторінки в ядрі: {insights.data.withoutPage.length}
                      </Text>
                      <Text className={classes.dim} data-testid="site-gsc-insights-low-ctr">
                        низький CTR: {insights.data.lowCtr.length}
                      </Text>
                    </Stack>
                  )}
                </Box>
              )}

              {/*
               * Акаунти GSC не мали жодного екрана взагалі — привʼязати перший property не
               * можна було без рядка в базі, вписаного руками.
               */}
              <Box className={classes.panel} data-testid="site-gsc-accounts-panel">
                <Group justify="space-between" mb={8}>
                  <Text className={classes.panelTitle}>Акаунти GSC</Text>
                  <CreatePanel
                    label="+ акаунт"
                    testId="gsc-account"
                    zone="site_seo"
                    open={gscAccountOpen}
                    onOpenChange={setGscAccountOpen}
                    busy={createGscAccount.isPending}
                    error={createGscAccount.error}
                    onReset={() => {
                      createGscAccount.reset();
                      gscAccountForm.reset();
                    }}
                    onSubmit={gscAccountForm.onSubmit((values) => {
                      createGscAccount.mutate(values);
                    })}
                  >
                    <TextInput
                      size="xs"
                      label="Назва"
                      data-testid="gsc-account-label"
                      style={{ width: 160 }}
                      {...gscAccountForm.getInputProps('label')}
                    />
                    <TextInput
                      size="xs"
                      label="Email"
                      data-testid="gsc-account-email"
                      style={{ width: 200 }}
                      {...gscAccountForm.getInputProps('email')}
                    />
                  </CreatePanel>
                </Group>
                {(gscAccounts.data ?? []).length === 0 ? (
                  <Text className={classes.dim} data-testid="site-gsc-accounts-empty">
                    Акаунтів немає
                  </Text>
                ) : (
                  <Stack gap={8}>
                    {(gscAccounts.data ?? []).map((account) => (
                      <Group
                        key={account.id}
                        gap={8}
                        wrap="wrap"
                        data-testid={`site-gsc-account-${account.email}`}
                      >
                        <Text className={classes.mono}>{account.email}</Text>
                        <Badge size="sm" color={account.connected ? 'mint' : 'graphite'}>
                          {account.connected ? 'зʼєднано' : 'без токена'}
                        </Badge>
                        <Button
                          size="compact-xs"
                          variant="default"
                          data-testid={`site-gsc-authorize-${account.email}`}
                          onClick={() => {
                            setAuthorizeError(null);
                            queryClient
                              .fetchQuery(
                                trpc.gsc.authorizeUrl.queryOptions({ accountId: account.id }),
                              )
                              .then((result) => {
                                setAuthorizeUrls((prev) => ({ ...prev, [account.id]: result.url }));
                              })
                              .catch((error: unknown) => {
                                setAuthorizeError(publicMessageOf(error));
                              });
                          }}
                        >
                          Отримати посилання авторизації
                        </Button>
                        {authorizeUrls[account.id] === undefined ? null : (
                          <a
                            href={authorizeUrls[account.id]}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`site-gsc-authorize-link-${account.email}`}
                          >
                            відкрити в антидетект-профілі
                          </a>
                        )}
                        <TextInput
                          size="xs"
                          placeholder="код авторизації"
                          data-testid={`site-gsc-oauth-code-${account.email}`}
                          style={{ width: 180 }}
                          value={oauthCode[account.id] ?? ''}
                          onChange={(event) => {
                            const { value } = event.currentTarget;
                            setOauthCode((prev) => ({ ...prev, [account.id]: value }));
                          }}
                        />
                        <Button
                          size="compact-xs"
                          disabled={(oauthCode[account.id] ?? '').trim() === ''}
                          loading={completeOAuth.isPending}
                          data-testid={`site-gsc-oauth-complete-${account.email}`}
                          onClick={() => {
                            const code = (oauthCode[account.id] ?? '').trim();
                            if (code !== '') completeOAuth.mutate({ accountId: account.id, code });
                          }}
                        >
                          Завершити авторизацію
                        </Button>
                      </Group>
                    ))}
                  </Stack>
                )}
                {authorizeError === null && completeOAuth.error === null ? null : (
                  <Text
                    className={classes.danger}
                    mt={6}
                    data-testid="site-gsc-accounts-error"
                    role="alert"
                  >
                    {authorizeError ?? publicMessageOf(completeOAuth.error)}
                  </Text>
                )}
              </Box>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="links" data-testid="site-panel-links">
            <Stack gap="md">
              <Box className={classes.panel} data-testid="site-links-panel">
                {plans.isPending ? (
                  <Loader size="sm" />
                ) : (plans.data ?? []).length === 0 ? (
                  <Text className={classes.dim} data-testid="site-links-empty">
                    Плану лінків немає — сайт не бере участі в лінкбілдингу.
                  </Text>
                ) : (
                  <Stack gap={6}>
                    {(plans.data ?? []).map((plan) => (
                      <Group key={plan.id} gap="lg" data-testid={`site-link-plan-${plan.id}`}>
                        <Text className={classes.dim}>
                          бюджет постів <span className={classes.mono}>{plan.budgetPosts}</span>
                        </Text>
                        <Text className={classes.dim}>
                          анкорів <span className={classes.mono}>{plan.anchorPct}%</span>
                        </Text>
                      </Group>
                    ))}
                  </Stack>
                )}
              </Box>

              {/* Гест-пости саме цього сайту — раніше видно було лише наскрізний реєстр. */}
              <Box className={classes.panel} data-testid="site-anchor-profile-panel">
                <Text className={classes.panelTitle} mb={8}>
                  Анкор-профіль домена
                </Text>
                {anchorProfile.error ? (
                  <Text className={classes.danger} data-testid="site-anchor-profile-error">
                    {publicMessageOf(anchorProfile.error)}
                  </Text>
                ) : (anchorProfile.data ?? []).length === 0 ? (
                  <Text className={classes.dim} data-testid="site-anchor-profile-empty">
                    Готових гест-постів на цей домен ще немає — профіль порахувати нема з чого.
                  </Text>
                ) : (
                  <Stack gap={4}>
                    {(anchorProfile.data ?? []).map((row) => (
                      <Group key={row.type} gap={10} data-testid={`site-anchor-${row.type}`}>
                        <Text className={classes.mono} fz="xs" style={{ width: 110 }}>
                          {row.type}
                        </Text>
                        <Progress value={row.sharePct} size="sm" color="mint" style={{ flex: 1 }} />
                        <Text className={classes.dim} style={{ width: 110 }}>
                          {row.count} · {row.sharePct}%
                        </Text>
                      </Group>
                    ))}
                  </Stack>
                )}
              </Box>

              <Box className={classes.panel} data-testid="site-guest-posts-panel">
                <Text className={classes.panelTitle} mb={8}>
                  Гест-пости
                </Text>
                {guestPosts.isPending ? (
                  <Loader size="sm" />
                ) : (guestPosts.data ?? []).length === 0 ? (
                  <Text className={classes.dim} data-testid="site-guest-posts-empty">
                    Гест-постів на цей сайт немає.
                  </Text>
                ) : (
                  <Table data-testid="site-guest-posts-table">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>тема</Table.Th>
                        <Table.Th>донор</Table.Th>
                        <Table.Th>статус</Table.Th>
                        <Table.Th>аудит</Table.Th>
                        <Table.Th>дії</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {(guestPosts.data ?? []).map((post) => (
                        <Table.Tr key={post.id} data-testid={`site-guest-post-${post.id}`}>
                          <Table.Td>{post.topic}</Table.Td>
                          <Table.Td className={classes.dim}>{post.donorDomain ?? '—'}</Table.Td>
                          <Table.Td>
                            <Badge size="sm" variant="default">
                              {post.status}
                            </Badge>
                          </Table.Td>
                          <Table.Td className={classes.dim}>{post.auditStatus ?? '—'}</Table.Td>
                          <Table.Td>
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              loading={auditPost.isPending && auditPost.variables?.id === post.id}
                              data-testid={`site-guest-post-audit-${post.id}`}
                              onClick={() => {
                                auditPost.mutate({ id: post.id });
                              }}
                            >
                              аудит
                            </Button>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
                {auditPost.error === null ? null : (
                  <Text
                    className={classes.danger}
                    mt={8}
                    data-testid="site-guest-posts-error"
                    role="alert"
                  >
                    {publicMessageOf(auditPost.error)}
                  </Text>
                )}
              </Box>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="log" data-testid="site-panel-log">
            <Box className={classes.panel} data-testid="site-log-panel">
              {history.isPending ? (
                <Loader size="sm" />
              ) : (history.data ?? []).length === 0 ? (
                <Text className={classes.dim} data-testid="site-log-empty">
                  Журнал порожній — із цим сайтом ще нічого не робили.
                </Text>
              ) : (
                <Table data-testid="site-card-log">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>коли</Table.Th>
                      <Table.Th>вид</Table.Th>
                      <Table.Th>що змінилось</Table.Th>
                      <Table.Th>хто</Table.Th>
                      <Table.Th>відкат</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {(history.data ?? []).map((entry) => {
                      /**
                       * Відкат — до снепшота «до» саме цього запису (`packages/core/src/audit.ts`
                       * пише його на кожну зміну), не до бекапу й не до масової операції: масова
                       * операція сьогодні не лишає слід у журналі ЦЬОГО сайту взагалі (пише один
                       * рядок на `bulk_operation`, не по сайту), тож кнопка тут чесно нічого не
                       * обіцяє для таких змін — лише для тих, що записав `sites.update`.
                       */
                      const patch =
                        entry.action === 'update'
                          ? revertPatch(diffBefore(entry.diff), {
                              entityType: site.entityType,
                              entityId: site.entityId,
                            })
                          : null;
                      return (
                        <Table.Tr key={entry.id} data-testid={`site-log-${entry.id}`}>
                          <Table.Td className={classes.mono}>
                            {new Date(entry.createdAt).toISOString().slice(0, 16).replace('T', ' ')}
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              size="sm"
                              variant="default"
                              data-testid={`site-log-kind-${entry.id}`}
                            >
                              {entryKind(entry.action, entry.diff)}
                            </Badge>
                          </Table.Td>
                          <Table.Td
                            className={classes.dim}
                            data-testid={`site-log-what-${entry.id}`}
                          >
                            {entryDescription(entry.action, entry.diff) || '—'}
                          </Table.Td>
                          <Table.Td className={classes.dim}>
                            {entry.authorName ?? 'система'}
                            {bulkOperationOf(entry.diff) === null ? null : (
                              <Button
                                size="compact-xs"
                                variant="subtle"
                                ml={6}
                                data-testid={`site-log-bulk-${entry.id}`}
                                onClick={() => {
                                  void navigate({
                                    to: '/bulk',
                                    search: {
                                      operationId: bulkOperationOf(entry.diff) ?? undefined,
                                    },
                                  });
                                }}
                              >
                                вся операція
                              </Button>
                            )}
                          </Table.Td>
                          <Table.Td>
                            {patch === null ? null : revertConfirmId === entry.id ? (
                              <Group gap={4} wrap="nowrap">
                                <Button
                                  size="compact-xs"
                                  color="rose"
                                  loading={siteUpdate.isPending}
                                  data-testid={`site-log-revert-confirm-${entry.id}`}
                                  onClick={() => {
                                    siteUpdate.mutate(
                                      { id: site.id, patch },
                                      { onSuccess: () => setRevertConfirmId(null) },
                                    );
                                  }}
                                >
                                  Так, відкатити
                                </Button>
                                <Button
                                  size="compact-xs"
                                  variant="subtle"
                                  data-testid={`site-log-revert-cancel-${entry.id}`}
                                  onClick={() => {
                                    setRevertConfirmId(null);
                                  }}
                                >
                                  Скасувати
                                </Button>
                              </Group>
                            ) : (
                              <Button
                                size="compact-xs"
                                variant="default"
                                data-testid={`site-log-revert-${entry.id}`}
                                onClick={() => {
                                  setRevertConfirmId(entry.id);
                                }}
                              >
                                Відкат
                              </Button>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              )}
              {siteUpdate.error === null ? null : (
                <Text
                  className={classes.danger}
                  mt={8}
                  data-testid="site-log-revert-error"
                  role="alert"
                >
                  {publicMessageOf(siteUpdate.error)}
                </Text>
              )}
            </Box>
          </Tabs.Panel>
        </Box>
      </Tabs>
    </Stack>
  );
}
